import invariant from 'invariant';
import { useMemo } from 'react';
import { sprintf } from 'sprintf-js';

import { ApiAddress } from 'api/types/ApiAddress';
import { ApiRemoteOptions } from 'api/types/ApiRemoteOptions';
import { Box } from 'components/Box/Box';
import { Divider } from 'components/Divider/Divider';
import { StyledNativeLinkWithRef } from 'components/Link/StyledNativeLinkWithRef';
import { H2 } from 'components/Text/H2';
import { ListingLocationLabel } from 'modules/listing/components/ListingLocationLabel/ListingLocationLabel';
import { spacing } from 'theme/theme';
import { getCityStateString } from 'utils/address/getCityStateString';
import { getCountryName, getUsStateName } from 'utils/internationalization';

type Props = {
  address: ApiAddress;
  listingName: string;
  orgName: string | undefined;
  orgUrl: Record<UserLocale, string> | null | undefined;
  remoteOptions: ApiRemoteOptions;
};

export function ListingViewSidebarTitle({
  address,
  listingName,
  orgName,
  orgUrl,
  remoteOptions,
}: Props) {
  const workLocation = useMemo(() => {
    if (remoteOptions.remoteZone === 'WORLD') {
      return getText('Anywhere in the World');
    }

    if (remoteOptions.remoteZone === 'COUNTRY') {
      return sprintf(getText('Anywhere in %(location)s'), {
        location: getCountryName(remoteOptions.remoteCountry || ''),
      });
    }

    if (remoteOptions.remoteZone === 'STATE') {
      invariant(remoteOptions.remoteState, 'Undefined remote state');
      return getUsStateName(remoteOptions.remoteState);
    }

    if (
      remoteOptions.locationType === 'ONSITE' ||
      remoteOptions.remoteZone === 'CITY'
    ) {
      return getCityStateString(address);
    }
  }, [
    address,
    remoteOptions.locationType,
    remoteOptions.remoteCountry,
    remoteOptions.remoteState,
    remoteOptions.remoteZone,
  ]);

  return (
    <div aria-hidden="true">
      <Box
        display="flex"
        flexDirection="column"
        alignItems="flex-start"
        mb={spacing[16]}
      >
        <H2 styleAs="h3" mb="0">
          {listingName}
        </H2>

        {orgName && orgUrl ? (
          <StyledNativeLinkWithRef
            href={orgUrl[CURRENT_LOCALE]}
            data-qa-id="org-link"
          >
            {orgName}
          </StyledNativeLinkWithRef>
        ) : null}
        {orgName && !orgUrl ? orgName : null}

        {workLocation ? (
          <Box mt={spacing[16]}>
            <ListingLocationLabel
              location={workLocation}
              type={remoteOptions.locationType}
            />
          </Box>
        ) : null}
      </Box>
      <Divider marginTop={spacing[24]} marginBottom={spacing[24]} />
    </div>
  );
}
